import useBridge from "@/hooks/useBridge";
import { useCallback, useState, useEffect, useRef } from "react";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import {
  useContractWrite,
  useSwitchNetwork,
  useWaitForTransaction,
  useNetwork,
} from "wagmi";
import { waitForTransaction, writeContract } from "wagmi/actions";
import {
  requestMintClaimerSignature,
  mintByClaimer,
} from "@/services/network/claimer";
import useAccount from "@/hooks/useAccount";
import stores, { actions } from "@/stores";
import ClaimerNFT from "@/chain-info/contracts/ClaimerNFT.json";
import ClaimerRelayer from "@/chain-info/contracts/ClaimerRelayer.json";
import { zksyncChainId } from "@/config/config";
import { EscrowDeposit } from "@/types/network";
import useMintHistory from "@/hooks/useMintHistory";
import useRequest from "@/hooks/useRequest";
import useClaimerContract from "@/hooks/useClaimerContract";
import useMintResult from "@/hooks/useMintResult";
import { useTypedSelector } from "@/stores";
import useMintReferral from "@/hooks/useMintReferral";
import mixpanel from "mixpanel-browser";
import { isSkipError } from "@/utils/validate";
import { ClaimerPaymentType } from "@/services/payment";
import { parseEther, parseUnits } from "viem";
import { KATContractInfo } from "@/services/contract";

function parseFormValidatorId(form: any) {
  const validatorId = !form.validator
    ? "13"
    : form.validator === "empty"
    ? "13"
    : form.validator;
  return validatorId;
}

export default function useMintClaimer() {
  const { address, isLogin, login } = useAccount();
  const { chain } = useNetwork();
  const { depositSuccess, depositing, karatDeposit, zkSyncDeposit } =
    useBridge();
  const [minting, setMinting] = useState(false);
  const { switchNetworkAsync } = useSwitchNetwork();
  const { form } = useVerifyClaim();
  const { history: mintHistory, status: mintStatus } = useMintHistory();
  const [mintId, setMintId] = useState<string | undefined>();
  const { lieutenantAddress, isValidLieutenant, hasParamLieutenant } =
    useMintReferral(zksyncChainId);
  const [nftMeta, setNftMeta] = useState<{
    score: number;
    role: number;
    validator: number;
  } | null>(null);

  const claimerContractInfo = useClaimerContract(zksyncChainId);

  const claimerContract = useTypedSelector((state) => {
    return state.devtool.config.claimerContract;
  });

  // const claimerContract = process.env
  //   .NEXT_PUBLIC_CLAIMER_RELAYER_CONTRACT as string;

  const mintResultRef = useRef<{
    timer: any;
    result: any;
    success: boolean;
    error: any;
  }>({
    timer: null,
    result: null,
    success: false,
    error: null,
  });

  const {
    data: mintResult,
    isSuccess: isMintSuccess,
    error: mintError,
  } = useMintResult(mintId);

  useEffect(() => {
    mintResultRef.current.result = mintResult;
    mintResultRef.current.success = isMintSuccess;
    mintResultRef.current.error = mintError;
  }, [mintResult, isMintSuccess, mintError]);

  useEffect(() => {
    return () => {
      mintResultRef.current.timer && clearInterval(mintResultRef.current.timer);
    };
  }, []);

  const { writeAsync: mintClaimerByContract, data: mintData } =
    useContractWrite({
      address: claimerContract as any,
      abi: ClaimerNFT.abi,
      functionName: "mintClaimerwithSig",
      chainId: zksyncChainId,
    });

  const { refetch: fetchTransaction } = useWaitForTransaction({
    hash: mintData?.hash,
    chainId: zksyncChainId,
  });

  const contractMintWithPay = useCallback(
    async (paymentType: ClaimerPaymentType, amount: number) => {
      setMinting(true);
      try {
        if (!isLogin) {
          login();
          return false;
        }
        if (chain?.id !== zksyncChainId) {
          // @ts-ignore
          await switchNetworkAsync(zksyncChainId as number);
        }
        if (paymentType === ClaimerPaymentType.KAT) {
          const resp = await writeContract({
            ...KATContractInfo,
            functionName: "approve",
            args: [claimerContract, parseUnits(String(amount), 18)],
          });
          try {
            await waitForTransaction({
              hash: resp.hash,
            });
          } catch (e: any) {
            if (!(await isSkipError(e))) {
              throw e;
            }
          }
        }
        console.log("form", form);
        const validatorId = parseFormValidatorId(form);
        console.log("validatorId", validatorId);
        const result = await requestMintClaimerSignature({
          walletAddress: address as any,
          validatorTokenId: validatorId,
          lieutenantAddr:
            lieutenantAddress || "0x0000000000000000000000000000000000000000",
        });

        setNftMeta({
          ...result.message,
          validator: validatorId,
        });

        const res = await mintClaimerByContract({
          args: [
            address,
            parseInt(validatorId || "0"),
            parseInt(result.message.score as any),
            lieutenantAddress || "0x0000000000000000000000000000000000000000",
            parseInt(result.message.role as any),
            result.signedMessage,
            // paymentType,
          ],
          // value:
          //   paymentType === ClaimerPaymentType.ETH
          //     ? parseEther(String(amount))
          //     : 0n,
        });

        try {
          const transReceipt = await waitForTransaction(res);
          if (transReceipt.status === "reverted") {
            throw new Error("Mint fail");
          }
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }

        mixpanel.track("claimer_minted", {
          walletAddress: address as any,
          validatorTokenId: validatorId,
          lieutenantAddr:
            lieutenantAddress || "0x0000000000000000000000000000000000000000",
          mintType: "contract",
        });
        stores.dispatch(
          actions.mint.setHistory({
            history: null,
            type: "claimer",
          })
        );
        return true;
      } finally {
        setMinting(false);
      }
    },
    [
      mintClaimerByContract,
      switchNetworkAsync,
      fetchTransaction,
      chain,
      address,
      zksyncChainId,
      isLogin,
      form,
      lieutenantAddress,
    ]
  );

  const contractMint = useCallback(async () => {
    setMinting(true);
    try {
      if (!isLogin) {
        login();
        return false;
      }
      if (chain?.id !== zksyncChainId) {
        // @ts-ignore
        await switchNetworkAsync(zksyncChainId as number);
      }
      console.log("form", form);
      const validatorId = parseFormValidatorId(form);
      console.log("validatorId", validatorId);
      const result = await requestMintClaimerSignature({
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
      });

      setNftMeta({
        ...result.message,
        validator: validatorId,
      });

      const res = await mintClaimerByContract({
        args: [
          address,
          parseInt(validatorId || "0"),
          parseInt(result.message.score as any),
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
          parseInt(result.message.role as any),
          result.signedMessage,
        ],
      });

      try {
        const transReceipt = await waitForTransaction(res);
        if (transReceipt.status === "reverted") {
          throw new Error("Mint fail");
        }
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }

      mixpanel.track("claimer_minted", {
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        mintType: "contract",
      });
      stores.dispatch(
        actions.mint.setHistory({
          history: null,
          type: "claimer",
        })
      );
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    mintClaimerByContract,
    switchNetworkAsync,
    fetchTransaction,
    chain,
    address,
    zksyncChainId,
    isLogin,
    form,
    lieutenantAddress,
  ]);

  const karatRecoverMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }

    setMinting(true);
    const validatorId = parseFormValidatorId(form);
    try {
      if (!form.recover_tx) {
        throw new Error("miss transaction hash");
      }
      mixpanel.track("recover_mint_claimer", {
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        depositChainId: form.recover_chain || 1,
        depositTransactionHash: form.recover_tx,
      });
      const result = await mintByClaimer({
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        depositChainId: form.recover_chain || 1,
        depositTransactionHash: form.recover_tx,
      });

      setNftMeta({
        ...result.message,
        validator: validatorId,
      });
      setMintId(result.mintId);
      stores.dispatch(
        actions.mint.clearHistory({
          tx: form.recover_tx,
        })
      );
      await new Promise((resolve, reject) => {
        mintResultRef.current.timer = setInterval(() => {
          console.log("check mint", mintResultRef);
          if (mintResultRef.current.error) {
            clearInterval(mintResultRef.current.timer);
            return reject(mintResultRef.current.error.message);
          }
          if (mintResultRef.current.success) {
            clearInterval(mintResultRef.current.timer);
            return resolve(true);
          }
        }, 1000);
      });
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    karatDeposit,
    chain,
    form,
    address,
    mintHistory,
    mintStatus,
    isLogin,
    lieutenantAddress,
  ]);

  const karatMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }

    let history;
    if (mintHistory && mintStatus === "success") {
      history = mintHistory;
    } else {
      const escrowDepositInfo = form?.karat as EscrowDeposit | undefined;
      let chainId = mintHistory?.chainId || escrowDepositInfo?.token?.chainId;
      if (chain?.id !== chainId) {
        // @ts-ignore
        await switchNetworkAsync(chainId);
      }
      history = await karatDeposit();
    }
    setMinting(true);
    await new Promise((resolve) => {
      setTimeout(resolve, 3000);
    });
    const validatorId = parseFormValidatorId(form);
    try {
      const result = await mintByClaimer({
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        depositChainId: history.chainId,
        depositTransactionHash: history.transaction,
      });
      setNftMeta({
        ...result.message,
        validator: validatorId,
      });
      setMintId(result.mintId);
      stores.dispatch(
        actions.mint.setHistory({
          history: null,
          type: "claimer",
        })
      );
      await new Promise((resolve, reject) => {
        mintResultRef.current.timer = setInterval(() => {
          console.log("check mint", mintResultRef);
          if (mintResultRef.current.error) {
            clearInterval(mintResultRef.current.timer);
            return reject(mintResultRef.current.error.message);
          }
          if (mintResultRef.current.success) {
            clearInterval(mintResultRef.current.timer);
            return resolve(true);
          }
        }, 1000);
      });
      mixpanel.track("claimer_minted", {
        walletAddress: address as any,
        validatorTokenId: validatorId,
        lieutenantAddr:
          lieutenantAddress || "0x0000000000000000000000000000000000000000",
        mintType: "one-click",
        chianId: history?.chainId,
      });
      return true;
    } finally {
      setMinting(false);
    }
  }, [
    karatDeposit,
    chain,
    form,
    address,
    mintHistory,
    mintStatus,
    isLogin,
    lieutenantAddress,
  ]);

  const zkSyncMint = useCallback(async () => {
    if (!isLogin) {
      login();
      return false;
    }
    const zksyncDepositInfo = form?.zksync as {
      chain: string;
      depositCost: number;
    };

    let history;
    if (mintHistory && mintStatus === "success") {
      history = mintHistory;
    } else {
      let chainId = String(mintHistory?.chainId || zksyncDepositInfo?.chain);
      if (chain?.id !== parseInt(chainId)) {
        // @ts-ignore
        await switchNetworkAsync(parseInt(chainId));
      }
      history = await zkSyncDeposit();
    }
    try {
      setMinting(true);
      await new Promise((resolve) => {
        setTimeout(resolve, 3000);
      });
      await contractMint();
    } finally {
      setMinting(false);
    }
    return true;
  }, [
    zkSyncDeposit,
    contractMint,
    form,
    switchNetworkAsync,
    mintHistory,
    mintStatus,
    isLogin,
  ]);

  let mintBtnText = "Deposit & Mint";
  if (mintHistory && mintStatus === "success") {
    mintBtnText = "Mint";
  }

  return {
    depositSuccess,
    depositing,
    history: mintHistory,
    minting,
    karatMint,
    zkSyncMint,
    contractMint,
    karatRecoverMint,
    claimerContractInfo,
    mintBtnText,
    defaultDepositCost: 0.02,
    nftMeta,
    isValidLieutenant,
    hasParamLieutenant,
    contractMintWithPay,
  };
}
